import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  MonitorSmartphone,
  Settings,
  Zap,
  ArrowRight,
  ShieldCheck,
  Lock,
  FileText,
  Code,
  Search,
  Menu,
  X,
  Globe,
  Server,
  Terminal,
  Network,
  Cloud,
  CloudLightning,
  LayoutPanelLeft,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import JsonLd, { generateOrganizationSchema, generateWebSiteSchema } from './JsonLd';
import { CertiniteBrandLink } from './CertiniteBrand';
import SslPromotion from './sslorder/SslPromotion';

const navTabBase =
  'inline-flex items-center gap-1.5 border-b-2 border-transparent px-3.5 py-4 text-[0.9375rem] font-normal leading-none text-[var(--vm-header-text)] transition-colors duration-200 hover:text-[var(--vm-header-teal)]';
const navTabActive = 'border-[var(--vm-header-teal)] text-[var(--vm-header-teal)] font-semibold';

/** VMware-style main bar CTAs: sharp corners, uppercase, teal */
const headerCtaBase =
  'inline-flex items-center justify-center rounded-none border-2 border-[var(--vm-header-teal)] px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.1em] transition-colors';

export default function PublicLayout() {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const closeDropdown = () => setActiveDropdown(null);

  const isTurkish = location.pathname.startsWith('/tr/') || location.pathname === '/tr';
  const isPricing = location.pathname === '/pricing' || location.pathname === '/tr/pricing';
  const isFeatures = location.pathname === '/features' || location.pathname === '/tr/features';
  const isLoginPage = location.pathname === '/login' || location.pathname === '/tr/login';
  const isRegisterPage = location.pathname === '/register' || location.pathname === '/tr/register';
  const isSolutions =
    location.pathname === '/enterprise' ||
    location.pathname === '/tr/enterprise' ||
    location.pathname === '/msps' ||
    location.pathname === '/tr/msps' ||
    location.pathname === '/enterprise-ssl' ||
    location.pathname === '/tr/kurumsal-ssl-yonetimi';
  const resourcePaths = ['/docs', '/blog', '/community'];
  const isResources = resourcePaths.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
  );

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const isTurkish = location.pathname.startsWith('/tr/') || location.pathname === '/tr';
    const targetLang = isTurkish ? 'tr' : 'en';
    if (i18n.resolvedLanguage !== targetLang) {
      i18n.changeLanguage(targetLang);
    }
  }, [location.pathname, i18n]);

  const lPath = (p: string) => {
    if (p.startsWith('http')) return p;
    const prefix = i18n.resolvedLanguage === 'tr' ? '/tr' : '';
    return prefix + (p === '/' ? '' : p) || '/';
  };

  const switchLang = (lang: 'en' | 'tr') => {
    closeDropdown();
    const currentPath = location.pathname;
    const localePairs: Record<string, string> = {
      '/enterprise-ssl': '/tr/kurumsal-ssl-yonetimi',
      '/tr/kurumsal-ssl-yonetimi': '/enterprise-ssl',
    };
    if (localePairs[currentPath]) {
      window.location.href = localePairs[currentPath];
      return;
    }
    if (lang === 'tr') {
      if (!currentPath.startsWith('/tr')) {
        window.location.href = `/tr${currentPath === '/' ? '' : currentPath}`;
      }
    } else if (currentPath.startsWith('/tr')) {
      window.location.href = currentPath.replace('/tr', '') || '/';
    }
  };

  const chevronNav = (open: boolean) =>
    `h-[15px] w-[15px] shrink-0 text-[var(--vm-header-text-muted)] transition-transform duration-200 ${open ? 'rotate-180 text-[var(--vm-header-teal)]' : ''}`;

  return (
    <div className="min-h-screen flex flex-col bg-[#f0f2f1] text-slate-800 selection:bg-[var(--vm-accent)]/25">
      <JsonLd data={[generateOrganizationSchema(), generateWebSiteSchema()]} />
      <header className="sticky top-0 z-50 w-full shadow-[0_1px_0_rgba(0,0,0,0.06)]">
        <SslPromotion locale={isTurkish ? 'tr' : 'en'} variant="strip" />
        {/* Main bar — white */}
        <div className="public-site-header bg-[var(--vm-header-main-bg)]">
          <div className="mx-auto flex min-h-[56px] max-w-7xl items-center justify-between px-4 sm:px-6">
            <div className="flex min-w-0 items-center gap-4 lg:gap-8">
              <CertiniteBrandLink to={lPath('/')} variant="onLight" size="md" className="py-2" onClick={closeDropdown} />

              <nav className="hidden items-stretch lg:flex">
                <Link to={lPath('/tools')} className={`${navTabBase} ${location.pathname.includes('/tools') ? navTabActive : ''}`} onClick={closeDropdown}>
                  {isTurkish ? 'Ücretsiz Araçlar' : 'Free SSL Tools'}
                </Link>
                <div
                  className="relative flex"
                  onMouseEnter={() => setActiveDropdown('products')}
                  onMouseLeave={closeDropdown}
                >
                  <button
                    type="button"
                    className={`group ${navTabBase} ${activeDropdown === 'products' || isFeatures ? navTabActive : ''}`}
                  >
                    {t('publicLayout.products')}
                    <ChevronDown className={chevronNav(activeDropdown === 'products')} aria-hidden />
                  </button>
                  {activeDropdown === 'products' && (
                    <div className="absolute left-0 top-full z-50 w-[800px] pt-0">
                      <div className="mt-0 rounded-b-lg border border-gray-200 bg-white p-6 shadow-xl">
                        <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                          <Link to={lPath('/products/centralized-management')} onClick={closeDropdown} className="group flex items-start">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#00587c]/10 transition-colors group-hover:bg-[#00587c]/15">
                              <MonitorSmartphone className="h-5 w-5 text-[var(--vm-header-teal)]" aria-hidden />
                            </div>
                            <div className="ml-4">
                              <h4 className="text-sm font-semibold text-[var(--vm-header-text)] group-hover:text-[var(--vm-header-teal)]">
                                {t('publicLayout.centralizedDashboard')}
                              </h4>
                              <p className="mt-1 text-xs leading-relaxed text-[var(--vm-header-text-muted)]">
                                {t('publicLayout.centralizedDashboardDesc')}
                              </p>
                            </div>
                          </Link>
                          <Link to={lPath('/products/windows-agent')} onClick={closeDropdown} className="group flex items-start">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#00587c]/10 transition-colors group-hover:bg-[#00587c]/15">
                              <Settings className="h-5 w-5 text-[var(--vm-header-teal)]" aria-hidden />
                            </div>
                            <div className="ml-4">
                              <h4 className="text-sm font-semibold text-[var(--vm-header-text)] group-hover:text-[var(--vm-header-teal)]">
                                {t('publicLayout.windowsAgent')}
                              </h4>
                              <p className="mt-1 text-xs leading-relaxed text-[var(--vm-header-text-muted)]">
                                {t('publicLayout.windowsAgentDesc')}
                              </p>
                            </div>
                          </Link>
                        </div>
                        <div className="mt-6 border-t border-gray-100 pt-6">
                          <h4 className="mb-4 text-xs font-bold uppercase tracking-wider text-slate-500">
                            {i18n.resolvedLanguage === 'tr' ? 'Entegrasyonlar' : 'Integrations & Platforms'}
                          </h4>
                          <div className="grid grid-cols-3 gap-y-4 gap-x-8">
                            <Link to={lPath('/integrations/iis')} onClick={closeDropdown} className="group flex items-center text-sm font-medium text-slate-700 hover:text-[var(--vm-header-teal)]">
                              <span className="flex h-6 w-6 items-center justify-center rounded bg-slate-100 mr-3 group-hover:bg-[#00587c]/10 text-slate-500 group-hover:text-[#00587c]">
                                <Server className="h-3.5 w-3.5" />
                              </span>
                              Windows IIS Server
                            </Link>
                            <Link to={lPath('/integrations/linux')} onClick={closeDropdown} className="group flex items-center text-sm font-medium text-slate-700 hover:text-[var(--vm-header-teal)]">
                              <span className="flex h-6 w-6 items-center justify-center rounded bg-slate-100 mr-3 group-hover:bg-[#00587c]/10 text-slate-500 group-hover:text-[#00587c]">
                                <Terminal className="h-3.5 w-3.5" />
                              </span>
                              Linux · Nginx
                            </Link>
                            <Link to={lPath('/integrations/apache')} onClick={closeDropdown} className="group flex items-center text-sm font-medium text-slate-700 hover:text-[var(--vm-header-teal)]">
                              <span className="flex h-6 w-6 items-center justify-center rounded bg-slate-100 mr-3 group-hover:bg-[#00587c]/10 text-slate-500 group-hover:text-[#00587c]">
                                <Terminal className="h-3.5 w-3.5" />
                              </span>
                              Linux · Apache
                            </Link>
                            <Link to={lPath('/integrations/tomcat')} onClick={closeDropdown} className="group flex items-center text-sm font-medium text-slate-700 hover:text-[var(--vm-header-teal)]">
                              <span className="flex h-6 w-6 items-center justify-center rounded bg-slate-100 mr-3 group-hover:bg-[#00587c]/10 text-slate-500 group-hover:text-[#00587c]">
                                <Terminal className="h-3.5 w-3.5" />
                              </span>
                              Linux · Tomcat
                            </Link>
                            <Link to={lPath('/integrations/ibm-http')} onClick={closeDropdown} className="group flex items-center text-sm font-medium text-slate-700 hover:text-[var(--vm-header-teal)]">
                              <span className="flex h-6 w-6 items-center justify-center rounded bg-slate-100 mr-3 group-hover:bg-[#00587c]/10 text-slate-500 group-hover:text-[#00587c]">
                                <Terminal className="h-3.5 w-3.5" />
                              </span>
                              Linux · IBM HTTP
                            </Link>
                            <Link to={lPath('/integrations/f5-big-ip')} onClick={closeDropdown} className="group flex items-center text-sm font-medium text-slate-700 hover:text-[var(--vm-header-teal)]">
                              <span className="flex h-6 w-6 items-center justify-center rounded bg-slate-100 mr-3 group-hover:bg-[#00587c]/10 text-slate-500 group-hover:text-[#00587c]">
                                <Network className="h-3.5 w-3.5" />
                              </span>
                              F5 BIG-IP
                            </Link>
                            <Link to={lPath('/integrations/citrix')} onClick={closeDropdown} className="group flex items-center text-sm font-medium text-slate-700 hover:text-[var(--vm-header-teal)]">
                              <span className="flex h-6 w-6 items-center justify-center rounded bg-slate-100 mr-3 group-hover:bg-[#00587c]/10 text-slate-500 group-hover:text-[#00587c]">
                                <Server className="h-3.5 w-3.5" />
                              </span>
                              Citrix ADC / NetScaler
                            </Link>
                            <Link to={lPath('/integrations/a10')} onClick={closeDropdown} className="group flex items-center text-sm font-medium text-slate-700 hover:text-[var(--vm-header-teal)]">
                              <span className="flex h-6 w-6 items-center justify-center rounded bg-slate-100 mr-3 group-hover:bg-[#00587c]/10 text-slate-500 group-hover:text-[#00587c]">
                                <Server className="h-3.5 w-3.5" />
                              </span>
                              A10 Thunder
                            </Link>
                            <Link to={lPath('/integrations/aws')} onClick={closeDropdown} className="group flex items-center text-sm font-medium text-slate-700 hover:text-[var(--vm-header-teal)]">
                              <span className="flex h-6 w-6 items-center justify-center rounded bg-slate-100 mr-3 group-hover:bg-[#00587c]/10 text-slate-500 group-hover:text-[#00587c]">
                                <Cloud className="h-3.5 w-3.5" />
                              </span>
                              AWS Cert Manager
                            </Link>
                            <Link to={lPath('/integrations/google-cloud')} onClick={closeDropdown} className="group flex items-center text-sm font-medium text-slate-700 hover:text-[var(--vm-header-teal)]">
                              <span className="flex h-6 w-6 items-center justify-center rounded bg-slate-100 mr-3 group-hover:bg-[#00587c]/10 text-slate-500 group-hover:text-[#00587c]">
                                <CloudLightning className="h-3.5 w-3.5" />
                              </span>
                              Google Cloud Cert Manager
                            </Link>
                            <Link to={lPath('/integrations/azure')} onClick={closeDropdown} className="group flex items-center text-sm font-medium text-slate-700 hover:text-[var(--vm-header-teal)]">
                              <span className="flex h-6 w-6 items-center justify-center rounded bg-slate-100 mr-3 group-hover:bg-[#00587c]/10 text-slate-500 group-hover:text-[#00587c]">
                                <Cloud className="h-3.5 w-3.5" />
                              </span>
                              Azure Key Vault
                            </Link>
                            <Link to={lPath('/integrations/cpanel')} onClick={closeDropdown} className="group flex items-center text-sm font-medium text-slate-700 hover:text-[var(--vm-header-teal)]">
                              <span className="flex h-6 w-6 items-center justify-center rounded bg-slate-100 mr-3 group-hover:bg-[#00587c]/10 text-slate-500 group-hover:text-[#00587c]">
                                <LayoutPanelLeft className="h-3.5 w-3.5" />
                              </span>
                              cPanel / WHM
                            </Link>
                            <Link to={lPath('/integrations/plesk')} onClick={closeDropdown} className="group flex items-center text-sm font-medium text-slate-700 hover:text-[var(--vm-header-teal)]">
                              <span className="flex h-6 w-6 items-center justify-center rounded bg-slate-100 mr-3 group-hover:bg-[#00587c]/10 text-slate-500 group-hover:text-[#00587c]">
                                <LayoutPanelLeft className="h-3.5 w-3.5" />
                              </span>
                              Plesk Obsidian
                            </Link>
                          </div>
                        </div>
                        <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4">
                          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                            {t('publicLayout.exploreAll')}
                          </span>
                          <Link
                            to={lPath('/features')}
                            onClick={closeDropdown}
                            className="flex items-center text-sm font-semibold text-[var(--vm-header-teal)] hover:underline"
                          >
                            {t('publicLayout.viewFeatures')} <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div
                  className="relative flex"
                  onMouseEnter={() => setActiveDropdown('solutions')}
                  onMouseLeave={closeDropdown}
                >
                  <button
                    type="button"
                    className={`group ${navTabBase} ${activeDropdown === 'solutions' || isSolutions ? navTabActive : ''}`}
                  >
                    {t('publicLayout.solutions')}
                    <ChevronDown className={chevronNav(activeDropdown === 'solutions')} aria-hidden />
                  </button>
                  {activeDropdown === 'solutions' && (
                    <div className="absolute left-0 top-full z-50 w-[320px] pt-0">
                      <div className="rounded-b-lg border border-gray-200 bg-white p-2 shadow-xl">
                        <Link
                          to={lPath('/enterprise')}
                          onClick={closeDropdown}
                          className="flex items-center rounded-md p-3 transition-colors hover:bg-gray-50"
                        >
                          <ShieldCheck className="h-5 w-5 shrink-0 text-[var(--vm-header-teal)]" aria-hidden />
                          <div className="ml-3">
                            <h4 className="text-sm font-semibold text-[var(--vm-header-text)]">{t('publicLayout.forEnterprise')}</h4>
                            <p className="text-xs text-[var(--vm-header-text-muted)]">{t('publicLayout.forEnterpriseDesc')}</p>
                          </div>
                        </Link>
                        <Link
                          to={isTurkish ? '/tr/kurumsal-ssl-yonetimi' : '/enterprise-ssl'}
                          onClick={closeDropdown}
                          className="mt-1 flex items-center rounded-md p-3 transition-colors hover:bg-gray-50"
                        >
                          <Lock className="h-5 w-5 shrink-0 text-[var(--vm-header-teal)]" aria-hidden />
                          <div className="ml-3">
                            <h4 className="text-sm font-semibold text-[var(--vm-header-text)]">{t('publicLayout.enterpriseSsl')}</h4>
                            <p className="text-xs text-[var(--vm-header-text-muted)]">{t('publicLayout.enterpriseSslDesc')}</p>
                          </div>
                        </Link>
                        <Link
                          to={lPath('/msps')}
                          onClick={closeDropdown}
                          className="mt-1 flex items-center rounded-md p-3 transition-colors hover:bg-gray-50"
                        >
                          <Zap className="h-5 w-5 shrink-0 text-[var(--vm-header-teal)]" aria-hidden />
                          <div className="ml-3">
                            <h4 className="text-sm font-semibold text-[var(--vm-header-text)]">{t('publicLayout.forMsps')}</h4>
                            <p className="text-xs text-[var(--vm-header-text-muted)]">{t('publicLayout.forMspsDesc')}</p>
                          </div>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>

                <Link
                  to={lPath('/pricing')}
                  className={`${navTabBase} ${isPricing ? navTabActive : ''}`}
                  onClick={closeDropdown}
                >
                  {t('publicLayout.howToBuy')}
                </Link>

                <div
                  className="relative flex"
                  onMouseEnter={() => setActiveDropdown('resources')}
                  onMouseLeave={closeDropdown}
                >
                  <button
                    type="button"
                    className={`group ${navTabBase} ${activeDropdown === 'resources' || isResources ? navTabActive : ''}`}
                  >
                    {t('publicLayout.resources')}
                    <ChevronDown className={chevronNav(activeDropdown === 'resources')} aria-hidden />
                  </button>
                  {activeDropdown === 'resources' && (
                    <div className="absolute right-0 top-full z-50 w-[280px] pt-0 lg:left-0">
                      <div className="rounded-b-lg border border-gray-200 bg-white p-2 shadow-xl">
                        <Link
                          to={lPath('/docs')}
                          onClick={closeDropdown}
                          className="block rounded-md px-3 py-2.5 text-sm font-medium text-[var(--vm-header-text)] hover:bg-gray-50"
                        >
                          {t('publicLayout.documentation')}
                        </Link>
                        <Link
                          to={lPath('/blog')}
                          onClick={closeDropdown}
                          className="block rounded-md px-3 py-2.5 text-sm font-medium text-[var(--vm-header-text)] hover:bg-gray-50"
                        >
                          {t('publicLayout.blog')}
                        </Link>
                        <Link
                          to={lPath('/community')}
                          onClick={closeDropdown}
                          className="block rounded-md px-3 py-2.5 text-sm font-medium text-[var(--vm-header-text)] hover:bg-gray-50"
                        >
                          {t('publicLayout.community')}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </nav>
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              {/* Language Selector */}
              <div
                className="relative hidden lg:flex items-center"
                onMouseEnter={() => setActiveDropdown('lang')}
                onMouseLeave={closeDropdown}
              >
                <button
                  type="button"
                  className={`inline-flex items-center gap-1 p-2 transition-colors ${activeDropdown === 'lang' ? 'text-[var(--vm-header-teal)]' : 'text-[var(--vm-header-text)] hover:text-[var(--vm-header-teal)]'}`}
                >
                  <Globe className="h-5 w-5" strokeWidth={2} />
                  <span className="text-[12px] font-semibold uppercase tracking-wider">{i18n.resolvedLanguage === 'tr' ? 'TR' : 'EN'}</span>
                </button>
                {activeDropdown === 'lang' && (
                  <div className="absolute right-0 top-full w-32 pt-0 z-50">
                    <div className="rounded-b-lg border border-gray-200 bg-white py-2 shadow-xl">
                      <button
                        className="block w-full text-left px-4 py-2 text-sm font-medium text-[var(--vm-header-text)] hover:bg-gray-50 hover:text-[var(--vm-header-teal)]"
                        onClick={() => switchLang('en')}
                      >
                        English
                      </button>
                      <button
                        className="block w-full text-left px-4 py-2 text-sm font-medium text-[var(--vm-header-text)] hover:bg-gray-50 hover:text-[var(--vm-header-teal)]"
                        onClick={() => switchLang('tr')}
                      >
                        Türkçe
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <Link
                to={lPath('/docs')}
                className="hidden p-2 text-[var(--vm-header-text)] transition-colors hover:text-[var(--vm-header-teal)] lg:inline-flex"
                title={t('publicLayout.documentation')}
                aria-label={t('publicLayout.documentation')}
                onClick={closeDropdown}
              >
                <Search className="h-5 w-5" strokeWidth={2} />
              </Link>
              <div className="hidden items-center gap-2 lg:flex">
                <Link
                  to={lPath('/login')}
                  className={`${headerCtaBase} bg-white text-[var(--vm-header-teal)] hover:bg-[rgba(0,88,124,0.08)] ${isLoginPage ? 'bg-[rgba(0,88,124,0.12)]' : ''}`}
                  onClick={closeDropdown}
                >
                  {t('publicLayout.login')}
                </Link>
                <Link
                  to={lPath('/register')}
                  className={`${headerCtaBase} text-white hover:bg-[var(--vm-header-teal-hover)] ${isRegisterPage ? 'bg-[#00415a]' : 'bg-[var(--vm-header-teal)]'}`}
                  onClick={closeDropdown}
                >
                  {t('publicLayout.signUp')}
                </Link>
              </div>
              <button
                type="button"
                className="inline-flex p-2 text-[var(--vm-header-text)] lg:hidden"
                aria-expanded={mobileOpen}
                aria-label="Menu"
                onClick={() => setMobileOpen((v) => !v)}
              >
                {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {mobileOpen && (
            <div className="border-t border-gray-200 bg-white px-4 py-4 lg:hidden">
              <nav className="flex flex-col gap-1 border-b border-gray-100 pb-4">
                <Link to={lPath('/tools')} className="py-2 text-sm font-medium text-[var(--vm-header-text)]" onClick={closeDropdown}>
                  {isTurkish ? 'Ücretsiz SSL Araçları' : 'Free SSL Tools'}
                </Link>
                <Link to={lPath('/features')} className="py-2 text-sm font-medium text-[var(--vm-header-text)]" onClick={closeDropdown}>
                  {t('publicLayout.products')}
                </Link>
                <Link to={lPath('/enterprise')} className="py-2 text-sm font-medium text-[var(--vm-header-text)]" onClick={closeDropdown}>
                  {t('publicLayout.solutions')}
                </Link>
                <Link
                  to={isTurkish ? '/tr/kurumsal-ssl-yonetimi' : '/enterprise-ssl'}
                  className="py-2 text-sm font-medium text-[var(--vm-header-text)]"
                  onClick={closeDropdown}
                >
                  {t('publicLayout.enterpriseSsl')}
                </Link>
                <Link to={lPath('/pricing')} className="py-2 text-sm font-medium text-[var(--vm-header-text)]" onClick={closeDropdown}>
                  {t('publicLayout.howToBuy')}
                </Link>
                <Link to={lPath('/docs')} className="py-2 text-sm font-medium text-[var(--vm-header-text)]" onClick={closeDropdown}>
                  {t('publicLayout.resources')}
                </Link>
              </nav>
              <div className="flex flex-col gap-2 pt-4">
                <Link
                  to={lPath('/register')}
                  className={`${headerCtaBase} py-3 text-white hover:bg-[var(--vm-header-teal-hover)] ${isRegisterPage ? 'bg-[#00415a]' : 'bg-[var(--vm-header-teal)]'}`}
                  onClick={closeDropdown}
                >
                  {t('publicLayout.signUp')}
                </Link>
                <Link
                  to={lPath('/login')}
                  className={`${headerCtaBase} bg-white py-3 text-[var(--vm-header-teal)] hover:bg-[rgba(0,88,124,0.08)] ${isLoginPage ? 'bg-[rgba(0,88,124,0.12)]' : ''}`}
                  onClick={closeDropdown}
                >
                  {t('publicLayout.login')}
                </Link>
                <Link to={lPath('/docs')} className="flex items-center justify-center gap-2 py-2 text-sm text-[var(--vm-header-teal)]" onClick={closeDropdown}>
                  <Search className="h-4 w-4" />
                  {t('publicLayout.documentation')}
                </Link>
              </div>
            </div>
          )}
        </div>

        <div className="public-site-header-gradient" aria-hidden />
      </header>

      <main className="flex w-full flex-1 flex-col items-center">
        <Outlet />
      </main>

      <footer className="w-full border-t border-slate-800 bg-[#1b1f1e] px-6 py-16 text-slate-300">
        <div className="mx-auto mb-12 grid max-w-7xl grid-cols-2 gap-8 md:grid-cols-6">
          <div className="col-span-2 md:col-span-2">
            <CertiniteBrandLink to={lPath('/')} variant="onDark" size="md" className="mb-6 opacity-90" />
            <p className="mb-6 max-w-xs text-sm leading-relaxed text-slate-400">{t('publicLayout.footerDesc')}</p>
            <div className="flex gap-4">
              <a
                href="https://github.com/certinite"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub Repository"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-slate-800 text-white transition-colors hover:bg-[var(--vm-accent)] hover:text-[#1b1f1e]"
              >
                <Code className="h-4 w-4" />
              </a>
              <Link
                to={lPath('/docs')}
                aria-label="Documentation"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-slate-800 text-white transition-colors hover:bg-[var(--vm-accent)] hover:text-[#1b1f1e]"
              >
                <FileText className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div>
            <h3 className="mb-4 font-semibold text-white">{t('publicLayout.product')}</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to={lPath('/features')} className="transition-colors hover:text-[var(--vm-accent)]">
                  {t('publicLayout.features')}
                </Link>
              </li>
              <li>
                <Link to={lPath('/features')} className="transition-colors hover:text-[var(--vm-accent)]">
                  {t('publicLayout.iisAgent')}
                </Link>
              </li>
              <li>
                <Link to={lPath('/pricing')} className="transition-colors hover:text-[var(--vm-accent)]">
                  {t('publicLayout.pricing')}
                </Link>
              </li>
              <li>
                <a
                  href="https://github.com/certinite"
                  className="transition-colors hover:text-[var(--vm-accent)]"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('publicLayout.changelog')}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="mb-4 font-semibold text-white">{t('publicLayout.solutions')}</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to={lPath('/enterprise')} className="transition-colors hover:text-[var(--vm-accent)]">
                  {t('publicLayout.forEnterprise')}
                </Link>
              </li>
              <li>
                <Link
                  to={isTurkish ? '/tr/kurumsal-ssl-yonetimi' : '/enterprise-ssl'}
                  className="transition-colors hover:text-[var(--vm-accent)]"
                >
                  {t('publicLayout.enterpriseSsl')}
                </Link>
              </li>
              <li>
                <Link to={lPath('/msps')} className="transition-colors hover:text-[var(--vm-accent)]">
                  {t('publicLayout.forMsps')}
                </Link>
              </li>
              <li>
                <Link to={lPath('/security')} className="transition-colors hover:text-[var(--vm-accent)]">
                  {t('publicLayout.security')}
                </Link>
              </li>
              <li>
                <Link to={lPath('/status')} className="transition-colors hover:text-[var(--vm-accent)]">
                  {t('publicLayout.uptime')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="mb-4 font-semibold text-white">{t('publicLayout.resources')}</h3>
            <ul className="space-y-3 text-sm">
              <li><Link to={lPath('/tools')} className="transition-colors hover:text-[var(--vm-accent)]">{isTurkish ? 'Ücretsiz SSL Araçları' : 'Free SSL Tools'}</Link></li>
              <li>
                <Link to={lPath('/docs')} className="transition-colors hover:text-[var(--vm-accent)]">
                  {t('publicLayout.documentation')}
                </Link>
              </li>
              <li>
                <Link to={lPath('/community')} className="transition-colors hover:text-[var(--vm-accent)]">
                  {t('publicLayout.community')}
                </Link>
              </li>
              <li>
                <Link to={lPath('/blog')} className="transition-colors hover:text-[var(--vm-accent)]">
                  {t('publicLayout.blog')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="mb-4 font-semibold text-white">{t('publicLayout.company')}</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to={lPath('/about')} className="transition-colors hover:text-[var(--vm-accent)]">
                  {t('publicLayout.about')}
                </Link>
              </li>
              <li>
                <Link to={lPath('/privacy')} className="transition-colors hover:text-[var(--vm-accent)]">
                  {t('publicLayout.legal')}
                </Link>
              </li>
              <li>
                <Link to={lPath('/terms')} className="transition-colors hover:text-[var(--vm-accent)]">
                  {t('publicLayout.terms')}
                </Link>
              </li>
              <li>
                <Link to={lPath('/refund')} className="transition-colors hover:text-[var(--vm-accent)]">
                  {t('publicLayout.refundPolicy')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 border-t border-slate-800 pt-8 text-sm text-slate-400 md:flex-row">
          <p>
            &copy; {new Date().getFullYear()} {t('publicLayout.rights')}
          </p>
          <div className="flex gap-6">
            <Link to={lPath('/status')} className="hover:text-[var(--vm-accent)]">
              {t('publicLayout.status')}
            </Link>
            <Link to={lPath('/security')} className="hover:text-[var(--vm-accent)]">
              {t('publicLayout.security')}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
